import { closeDrawer, isOpenInfoModal, isReportDrawerOpen, reportFormDataInnit, reportListData, selectedInfoModalTab } from 'Pages/ReportsAlerts/Reports/@signals'
import { INFO_MODAL_TABS } from 'Pages/ReportsAlerts/Reports/@constants'

// ─── DOM helpers ────────────────────────────────────────────────────────────

const q = id => document.querySelector(`[data-tour-id="${id}"]`)

function findByText(text, exact = false) {
	const els = document.querySelectorAll('span, p, label, h6, h5, h4, .MuiTypography-root')
	for (const el of els) {
		const content = el.textContent.trim().toLowerCase()
		if (exact ? content === text.toLowerCase() : content.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function findTableColumn(headerText, exact = false) {
	const headers = document.querySelectorAll('th, [class*="MUIDataTableHeadCell"]')
	for (const h of headers) {
		const content = h.textContent.trim().toLowerCase()
		if (exact ? content === headerText.toLowerCase() : content.includes(headerText.toLowerCase())) {
			return h
		}
	}
	return null
}

// ─── Language detection ──────────────────────────────────────────────────────

const ES = navigator.language?.startsWith('es')

// Close drawer on tour skip/close during drawer section
const drawerCleanup = () => closeDrawer()

// ─── Steps ───────────────────────────────────────────────────────────────────

export const TOUR_STEPS = [
	// ─── 0: Intro ───────────────────────────────────────────────────────────────
	{
		type: 'intro',
		icon: 'route',
		title: ES ? 'Bienvenido a Extras y protecciones' : 'Welcome to Extras & Protections',
		description: ES
			? 'Los informes de Extras y protecciones recogen, para cada coche encontrado en la web propia de la empresa de alquiler, el precio de sus extras y condiciones: proteccion (Basic, Medium, Premium, Zero), asistencia en carretera, GPS, sillas infantiles, wifi, conductor adicional y joven, kilometraje, PAI, cadenas de nieve, paso de frontera y mas. Las rutas de solo ida son un campo opcional.'
			: 'Extras & Protections reports collect, for every car found on the rental company\'s own website, the price of its extras and conditions: protection (Basic, Medium, Premium, Zero), roadside assistance, GPS, child seats, wifi, additional and young driver, mileage, PAI, snow chains, cross-border and more. One-way routes are only an optional field.',
	},
	// ─── 1: Icons Legend ────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const legend = document.querySelector('[data-tour-id=\"tour-icons-legend\"]')
			if (legend) return legend
			const el = findByText('Download Report')
			if (!el) return null
			let parent = el.parentElement
			for (let i = 0; i < 6; i++) {
				if (!parent) break
				const text = parent.textContent.toLowerCase()
				if (text.includes('download report') && text.includes('download export')) {
					if (parent.children.length >= 3) return parent
				}
				parent = parent.parentElement
			}
			return el.parentElement
		},
		icon: 'icons',
		title: ES ? 'Leyenda de Iconos' : 'Icons Legend',
		description: ES
			? 'La leyenda muestra los iconos de descarga disponibles en la columna Fecha. Los informes de Extras y protecciones se centran en la recopilacion de datos — descarga el informe completo o la exportacion filtrada una vez finalizada la ejecucion.'
			: 'The legend shows download icons available in the Date column. Extras & Protections reports focus on data collection — download the full report or the filtered export after execution completes.',
		example: ES
			? 'Descargar Informe | Descargar Exportacion | Multi-informe | Multi-exportacion'
			: 'Download Report | Download Export | Multi-report | Multi-export',
	},
	// ─── 2: Create Extras & Protections Report — opens the drawer ──────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			if (q('tour-create-report')) return q('tour-create-report')
			const btn = findByText('Create Report')
			return btn?.closest('button') || btn?.parentElement
		},
		icon: 'file-plus',
		title: ES ? 'Crear informe de Extras y protecciones' : 'Create Extras & Protections Report',
		description: ES
			? 'Este boton abre el formulario de creacion de un informe de extras y protecciones. Pulsa «Next» y lo abriremos para recorrer cada campo juntos: nombre, sitios, oficinas, rutas de solo ida opcionales, fechas, filtros y frecuencia.'
			: 'This button opens the creation form of an extras and protections report. Press "Next" and we will open it and walk through each field together: name, sites, offices, optional one-way routes, dates, filters, and frequency.',
		action: () => reportFormDataInnit(),
		waitFor: () => isReportDrawerOpen.value === true,
	},
	// ─── 3: Drawer overview ─────────────────────────────────────────────────────
	{
		id: 'drawer-start',
		type: 'intro',
		icon: 'rectangle-list',
		title: ES ? 'Formulario de Creacion' : 'Creation Form',
		description: ES
			? 'El formulario tiene cinco secciones: Nombre, Sitios y Oficinas, Configuracion de Busqueda, Filtros de Exportacion y Frecuencia. Vamos campo por campo.'
			: 'The form has five sections: Name, Sites & Offices, Search Configuration, Export Filters, and Frequency. Let\'s go field by field.',
		reverseAction: () => closeDrawer(),
		cleanup: drawerCleanup,
	},
	// ─── 4: Num. Searches info button ────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-num-searches-info"]'),
		icon: 'circle-info',
		title: ES ? 'Informacion de Busquedas' : 'Search Info',
		description: ES
			? 'El indicador "Num. Searches" muestra el total de busquedas que ejecutara este informe. Pulsa el icono para ver el desglose detallado: numero de oficinas, busquedas por oficina, fechas de recogida primera y ultima, y el listado completo de fechas de pickup.'
			: 'The "Num. Searches" indicator shows the total searches this report will execute. Click the info icon to see the detailed breakdown: number of offices, searches per office, first and last pickup dates, and the full pickup date list.',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 1 — Report Name
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 5: Report Name field ───────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-report-name"]'),
		icon: 'pen-line',
		title: ES ? 'Nombre del Informe' : 'Report Name',
		description: ES
			? 'El nombre que identifica este informe en la lista. Usa nombres descriptivos que incluyan las oficinas o el mercado, por ejemplo "Extras Aeropuerto Madrid Semanal". Minimo 5 caracteres, maximo 100.'
			: 'The name that identifies this report in the list. Use descriptive names that include the offices or the market, e.g. "Madrid Airport Extras Weekly". Minimum 5 characters, maximum 100.',
		example: ES ? 'Ej: Madrid → Barcelona Airport Weekly' : 'E.g.: Madrid → Barcelona Airport Weekly',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 2 — Sites & Offices
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 6: Use Office Group switch ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-office-switch"]'),
		icon: 'toggle-off',
		title: ES ? 'Modo de Oficina' : 'Office Mode',
		description: ES
			? 'Elige como seleccionar las oficinas de recogida. «Offices»: seleccionas oficinas individuales. «Office group»: usas un grupo de oficinas predefinido que cubre varias ubicaciones. Los grupos son mas practicos para monitorizar mercados completos.'
			: 'Choose how to select pickup offices. "Offices": pick individual offices. "Office group": use a predefined office group covering multiple locations. Groups are more practical for monitoring entire markets.',
		example: ES ? 'Office group = Grupo | Offices = Oficinas individuales' : 'Office group = Group | Offices = Individual offices',
		cleanup: drawerCleanup,
	},
	// ─── 7: Pickup Office / Group Office ────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-office-field"]'),
		icon: 'building',
		title: ES ? 'Oficinas de Recogida' : 'Pickup Offices',
		description: ES
			? 'Las oficinas (o el grupo) de recogida que quieres monitorizar. Los extras y protecciones se recogen para cada coche encontrado en estas oficinas. El numero de oficinas multiplicado por sitios y fechas determina el total de busquedas.'
			: 'The pickup offices (or group) you want to monitor. Extras and protections are collected for every car found at these offices. The number of offices multiplied by sites and dates determines the total searches.',
		cleanup: drawerCleanup,
	},
	// ─── 8: Sites ───────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-site"]'),
		icon: 'globe',
		title: ES ? 'Sitios Web' : 'Sites',
		description: ES
			? 'Las webs propias de las empresas de alquiler que quieres monitorizar. No todos los sitios permiten recoger sus extras — aqui solo aparecen los que admiten informes de extras y protecciones.'
			: 'The rental companies\' own websites to monitor. Not every site lets us collect its extras — only the ones that support extras and protections reports are listed here.',
		cleanup: drawerCleanup,
	},
	// ─── 9: Currency ────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-currency"]'),
		icon: 'euro-sign',
		title: ES ? 'Moneda' : 'Currency',
		description: ES
			? 'La moneda en la que se mostraran los precios en los resultados del informe. Selecciona la moneda que corresponda al mercado de las oficinas que estas monitorizando.'
			: 'The currency in which prices will be shown in the report results. Select the currency that matches the market of the offices you are monitoring.',
		cleanup: drawerCleanup,
	},
	// ─── 10: One-Way Routes field ─────────────────────────────────────────────────
	{
		type: 'field',
		// the "One way routes" link while it is closed, the whole block once it shows the field
		findTarget: () => q('tour-oneway')?.querySelector('[aria-expanded="false"]') || q('tour-oneway'),
		icon: 'right-long',
		title: ES ? 'Rutas de Solo Ida (Opcional)' : 'One-Way Routes (Optional)',
		description: ES
			? 'Campo opcional. Abre «One way routes» para elegir las «One Way Office Routes»: oficinas de devolucion que, combinadas con la de recogida, se buscan ademas del alquiler de ida y vuelta. Si lo dejas vacio, el informe recoge los extras del alquiler en la misma oficina.'
			: 'Optional field. Open "One way routes" to choose the "One Way Office Routes": dropoff offices that, paired with the pickup office, are searched in addition to the round trip. Left empty, the report collects the extras of the rental at the same office.',
		example: ES ? 'MAD (Madrid) → BCN (Barcelona)' : 'MAD (Madrid) → BCN (Barcelona)',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 3 — Search Configuration
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 11: Use CSV switch ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-csv-switch"]'),
		icon: 'file-csv',
		title: ES ? 'Modo de Busqueda: Fecha o CSV' : 'Search Mode: Date or CSV',
		description: ES
			? 'Con «Date setup» (por defecto) el informe genera busquedas a partir de la configuracion de fechas. Si eliges «CSV file», puedes subir un fichero con busquedas completamente personalizadas — util para rutas especificas que no caben en la configuracion estandar.'
			: 'With "Date setup" (the default) the report generates searches from the date configuration. If you choose "CSV file", you can upload a file with fully custom searches — useful for specific routes that do not fit the standard configuration.',
		cleanup: drawerCleanup,
	},
	// ─── 12: Pickup Dates mode selector ─────────────────────────────────────────
	{
		type: 'field',
		// the mode selector (first field of the row); the row itself as a fallback
		findTarget: () => q('tour-pickup-dates')?.querySelector('.MuiFormControl-root') || q('tour-pickup-dates'),
		icon: 'calendar-range',
		title: ES ? 'Modo de Fechas de Recogida' : 'Pickup Dates Mode',
		description: ES
			? 'Define como se seleccionan las fechas de recogida. Cuatro opciones: dias especificos, lead times (x dias desde hoy), dia y semana, o proximo mes completo. Cada modo genera un subformulario diferente adaptado al tipo de ruta.'
			: 'Defines how pickup dates are selected. Four options: specific days, lead times (x days from today), day and week, or next full month. Each mode shows a different sub-form adapted to the route type.',
		example: ES
			? 'Lead times: 1,3,7,14,30 dias | Especificos: 15/06, 22/06...'
			: 'Lead times: 1,3,7,14,30 days | Specific: 15/06, 22/06...',
		cleanup: drawerCleanup,
	},
	// ─── 13: Rental length ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-rental-length"]'),
		icon: 'moon',
		title: ES ? 'Duracion del Alquiler' : 'Rental Length',
		description: ES
			? 'El numero de noches del alquiler que se buscaran. Puedes seleccionar varias duraciones a la vez. Cada duracion combinada con cada fecha genera una busqueda independiente.'
			: 'The number of rental nights to search. You can select multiple durations at once. Each duration combined with each date generates an independent search.',
		example: ES ? 'Ej: 1, 3, 7 noches → 3x busquedas por fecha' : 'E.g.: 1, 3, 7 nights → 3x searches per date',
		cleanup: drawerCleanup,
	},
	// ─── 14: Days to monitor ────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-days-monitor"]'),
		icon: 'calendar-days',
		title: ES ? 'Dias a Monitorizar' : 'Days to Monitor',
		description: ES
			? 'Cuantos dias consecutivos a partir de hoy incluira el informe. Por ejemplo, 30 dias significa que se buscaran precios para los proximos 30 dias de recogida. Maximo 365 dias.'
			: 'How many consecutive days from today the report will include. For example, 30 days means prices will be searched for the next 30 pickup days. Maximum 365 days.',
		cleanup: drawerCleanup,
	},
	// ─── 15: Pickup Time / Dropoff Time ─────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-pickup-time"]'),
		icon: 'clock',
		title: ES ? 'Hora de Recogida' : 'Pickup Time',
		description: ES
			? 'La hora de recogida («Pickup Time») que se usara en las busquedas. Configurala para obtener precios comparables entre los sitios monitorizados.'
			: 'The pickup time ("Pickup Time") used in searches. Configure it to get comparable prices across the monitored sites.',
		example: ES ? 'Recogida: 10:00' : 'Pickup: 10:00',
		cleanup: drawerCleanup,
	},
	// ─── 15b: Dropoff Time ──────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => q('tour-dropoff-time'),
		icon: 'clock',
		title: ES ? 'Hora de Devolucion' : 'Dropoff Time',
		description: ES
			? 'La hora de devolucion («Dropoff Time») de las busquedas. Junto con la hora de recogida define el periodo de alquiler que se busca en cada sitio.'
			: 'The dropoff time ("Dropoff Time") of the searches. Together with the pickup time it defines the rental period searched on each site.',
		example: ES ? 'Devolucion: 10:00' : 'Dropoff: 10:00',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 4 — Export Filters (optional)
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 16: Export Filters section ─────────────────────────────────────────────
	{
		type: 'field',
		// the section's title: the whole open section is too tall to leave room for the card
		findTarget: () => q('tour-export-filters')?.querySelector('h3') || q('tour-export-filters'),
		icon: 'sliders',
		title: ES ? 'Filtros de Exportacion (Opcional)' : 'Export Filters (Optional)',
		description: ES
			? 'Seccion opcional para filtrar los resultados del Excel. Con el selector «Categories / Acriss» eliges filtrar por categoria de vehiculo o por codigo ACRISS. Tambien puedes establecer un limite de horas y activar la generacion automatica del informe tras cada ejecucion.'
			: 'Optional section to filter Excel results. The "Categories / Acriss" selector chooses between filtering by vehicle category or ACRISS code. You can also set an hours limit and enable automatic report generation after each execution.',
		example: ES ? 'Categorias: Economy, Compact | ACRISS: ECMR, CCMR' : 'Categories: Economy, Compact | ACRISS: ECMR, CCMR',
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// SECTION 5 — Frequency & Notification
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 17: Email ──────────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-email"]'),
		icon: 'envelope',
		title: ES ? 'Email de Notificacion' : 'Notification Email',
		description: ES
			? 'Los correos electronicos a los que se enviaran los resultados del informe cuando se complete la ejecucion. Puedes anadir varios emails — pulsa Enter o escribe una coma entre cada uno. Por defecto se rellena con tu email de usuario.'
			: 'The email addresses that will receive report results when execution completes. You can add multiple emails — press Enter or type a comma between each. Defaults to your user email.',
		cleanup: drawerCleanup,
	},
	// ─── 18: Execution frequency (hours) ────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-exec-frequency"]'),
		icon: 'clock-rotate-left',
		title: ES ? 'Horas de Ejecucion' : 'Execution Hours',
		description: ES
			? 'Las horas del dia en las que se ejecutara automaticamente el informe. Cada ejecucion vuelve a recoger los precios de extras y protecciones en todos los sitios.'
			: 'The times of day when the report will run automatically. Each execution collects the extras and protections prices again on every site.',
		example: ES ? 'Ej: 08:00, 18:00 → dos capturas al dia' : 'E.g.: 08:00, 18:00 → two daily snapshots',
		cleanup: drawerCleanup,
	},
	// ─── 19: Frequencies (days) + close drawer ───────────────────────────────────
	{
		id: 'drawer-end',
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-frequencies"]'),
		icon: 'calendar-week',
		title: ES ? 'Dias de Ejecucion' : 'Execution Days',
		description: ES
			? 'Los dias de la semana en los que se ejecutara el informe. Combinado con las horas de ejecucion define el calendario completo. Cuando lo tengas todo configurado, guarda con el boton "Save" del pie del formulario.'
			: 'The days of the week when the report will run. Combined with execution hours this defines the complete schedule. When everything is configured, save using the "Save" button at the bottom of the form.',
		example: ES ? 'Lunes, Miercoles, Viernes' : 'Monday, Wednesday, Friday',
		action: () => closeDrawer(),
		waitFor: () => !isReportDrawerOpen.value,
		cleanup: drawerCleanup,
	},
	// ═══════════════════════════════════════════════════════════════════════════
	// TABLE SECTION
	// ═══════════════════════════════════════════════════════════════════════════
	// ─── 20: Extras & Protections Table ────────────────────────────────────────────────
	{
		type: 'field',
		// the table card (real or demo), found from its status selector
		findTarget: () => q('tour-status-toggle')?.closest('.MuiPaper-root') || document.querySelector('.MuiTable-root')?.closest('.MuiPaper-root'),
		icon: 'table-cells',
		title: ES ? 'Tabla de Extras y protecciones' : 'Extras & Protections Table',
		description: ES
			? 'Todos tus informes de extras y protecciones listados aqui. Cada fila muestra la configuracion del informe incluyendo las oficinas, los sitios monitorizados, la programacion y el estado de ejecucion actual.'
			: 'All your extras and protections reports listed here. Each row shows the report configuration including the offices, monitored sites, schedule, and current execution status.',
	},
	// ─── 20b: Enabled / Disabled / All selector ─────────────────────────────────
	{
		type: 'field',
		findTarget: () => q('tour-status-toggle'),
		icon: 'filter',
		title: ES ? 'Informes Mostrados' : 'Reports Shown',
		description: ES
			? 'Elige que informes lista la tabla: «Enabled» (activos), «Disabled» (desactivados) o «All» (todos). Por defecto ves los activos.'
			: 'Choose which reports the table lists: "Enabled", "Disabled" or "All". By default you see the enabled ones.',
		example: ES ? 'Enabled | Disabled | All' : 'Enabled | Disabled | All',
	},
	// ─── 21: Report Name column ─────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Report name'),
		icon: 'tag',
		title: ES ? 'Nombre del Informe' : 'Report Name',
		description: ES
			? 'El nombre de cada informe de extras y protecciones. Usa nombres claros que incluyan las oficinas o el mercado (p. ej., "Extras Aeropuerto Madrid Semanal") para identificar rapidamente cada informe en la lista.'
			: 'The name of each extras and protections report. Use clear naming that includes the offices or the market (e.g., "Madrid Airport Extras Weekly") to quickly identify each report in the list.',
	},
	// ─── 22: Search numbers column ──────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Search numbers') || findTableColumn('Searches'),
		icon: 'hashtag',
		title: ES ? 'Numero de Busquedas' : 'Search Numbers',
		description: ES
			? 'Total de busquedas por ejecucion: oficinas × sitios × fechas de recogida × duraciones; cada ruta de solo ida opcional cuenta como una oficina mas. Los informes con mas de 1.000 muestran un icono de fuego como advertencia.'
			: 'Total searches per execution: offices × sites × pickup dates × durations; each optional one-way route counts as one more office. Reports with 1,000+ show a fire icon warning.',
		example: ES
			? 'Menos de 1K = Normal | 1K+ = Advertencia de fuego'
			: 'Under 1K = Normal | 1K+ = Fire warning',
	},
	// ─── 23: Sites column ───────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Sites'),
		icon: 'globe',
		title: ES ? 'Sitios' : 'Sites',
		description: ES
			? 'Las webs propias de las empresas de alquiler de las que se recogen los precios de extras y protecciones. Solo se pueden elegir los sitios que admiten informes de extras.'
			: 'The rental companies\' own websites whose extras and protections prices are collected. Only sites that support extras reports can be chosen.',
	},
	// ─── 24: Frequency Hours column ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Frequency Hours') || findTableColumn('Hours'),
		icon: 'clock',
		title: ES ? 'Horas de Frecuencia' : 'Frequency Hours',
		description: ES
			? 'Horas de ejecucion programadas. En cada una se vuelven a recoger los precios de extras y protecciones.'
			: 'Scheduled execution times. Each one collects the extras and protections prices again.',
	},
	// ─── 25: Frequency Days column ──────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Frequency', true),
		icon: 'calendar-days',
		title: ES ? 'Dias de Frecuencia' : 'Frequency Days',
		description: ES
			? 'Dias de la semana en los que se ejecuta el informe. Combinado con las horas, esto define el calendario de monitorizacion completo del informe.'
			: 'Days of the week when the report executes. Combined with the hours, this defines the complete monitoring schedule of the report.',
	},
	// ─── 26: Pick up & Drop off route column ────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Pick up') || findTableColumn('Airports'),
		icon: 'location-dot',
		title: ES ? 'Ruta de Recogida y Devolucion' : 'Pick up & Drop off Route',
		description: ES
			? 'Muestra las oficinas (o el grupo) de recogida del informe y, debajo, la de devolucion: la misma, salvo que se hayan anadido rutas de solo ida opcionales.'
			: 'Shows the report\'s pickup offices (or group) and, below, the dropoff office: the same one, unless optional one-way routes were added.',
		example: ES
			? 'Aeropuerto de Madrid (MAD) → Aeropuerto de Barcelona (BCN)'
			: 'Madrid Airport (MAD) → Barcelona Airport (BCN)',
	},
	// ─── 27: Date & Execution Status column ─────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Date'),
		icon: 'calendar-check',
		title: ES ? 'Fecha y Estado de Ejecucion' : 'Date & Execution Status',
		description: ES
			? 'Ultima fecha de ejecucion y estado: Listo (completado, descarga disponible), En ejecucion (barra de progreso con contador de tareas) o Detenido. Haz clic en la barra de progreso durante la ejecucion para ver los detalles de las busquedas.'
			: 'Last execution date and status: Ready (completed, download available), Running (progress bar with task count), or Stopped. Click the progress bar during execution to view search details.',
		example: ES
			? 'Listo = Descargas | En ejecucion = Progreso | Detenido = Parado'
			: 'Ready = Downloads | Running = Progress | Stopped = Halted',
	},
	// ─── 28: Download Report button ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-report"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-report"]'),
		icon: 'cloud-arrow-down',
		title: ES ? 'Descargar Informe' : 'Download Report',
		description: ES
			? 'El icono verde descarga el informe completo en Excel con todos los datos de la ejecucion. Disponible solo cuando el estado es "Listo". El fichero incluye todos los precios encontrados ordenados por fecha, sitio y tipo de vehiculo.'
			: 'The green icon downloads the full report in Excel with all execution data. Available only when the status is "Ready". The file includes all prices found sorted by date, site, and vehicle type.',
	},
	// ─── 29: Download Export button ──────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => document.querySelector('[data-tour-id="tour-date-btn-export"]'),
		skipIf: () => !document.querySelector('[data-tour-id="tour-date-btn-export"]'),
		icon: 'file-arrow-down',
		title: ES ? 'Descargar Exportacion' : 'Download Export',
		description: ES
			? 'El icono azul descarga la exportacion filtrada segun los filtros de exportacion configurados (categorias o ACRISS). Util cuando solo necesitas un subconjunto de los datos de la ejecucion, ya filtrado y listo para analizar.'
			: 'The blue icon downloads the filtered export according to the export filters configured (categories or ACRISS). Useful when you only need a subset of the execution data, already filtered and ready to analyze.',
	},
	// ─── 29b: Download Multi-Report button ──────────────────────────────────────
	{
		type: 'field',
		findTarget: () => q('tour-date-btn-multireport'),
		skipIf: () => !q('tour-date-btn-multireport'),
		icon: 'cloud-arrow-down',
		title: ES ? 'Descargar Multi-Informe' : 'Download Multi-Report',
		description: ES
			? 'El icono rosa genera y descarga el informe Excel completo de la ejecucion, con una hoja por cada combinacion de sitio y oficina y todos los precios encontrados.'
			: 'The pink icon generates and downloads the complete Excel report of the execution, with one sheet per site and office combination and every price found.',
	},
	// ─── 29c: Download Multi-Export button ──────────────────────────────────────
	{
		type: 'field',
		findTarget: () => q('tour-date-btn-multiexport'),
		skipIf: () => !q('tour-date-btn-multiexport'),
		icon: 'file-arrow-down',
		title: ES ? 'Descargar Multi-Exportacion' : 'Download Multi-Export',
		description: ES
			? 'El icono morado descarga el fichero de exportacion de la ejecucion aplicando los filtros de exportacion del informe (categorias o ACRISS). Ideal para alimentar sistemas externos o herramientas de analisis propias.'
			: 'The purple icon downloads the execution\'s export file with the report\'s export filters applied (categories or ACRISS). Ideal for feeding external systems or your own analysis tools.',
	},
	// ─── 30: Enable / Disable toggle ────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const sw = document.querySelector('.MuiSwitch-root')
			if (sw) return sw.closest('td') || sw
			return findTableColumn('Off/On') || findTableColumn('On')
		},
		icon: 'toggle-on',
		title: ES ? 'Activar / Desactivar' : 'Enable / Disable',
		description: ES
			? 'Activa o desactiva un informe. Los informes desactivados conservan su configuracion pero no se ejecutaran segun la programacion. Util para oficinas de temporada que solo necesitas en periodos de alta demanda.'
			: 'Toggle a report on or off. Disabled reports keep their configuration but won\'t execute on schedule. Useful for seasonal offices you only need during peak periods.',
	},
	// ─── 31: Row Actions ────────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => findTableColumn('Actions'),
		icon: 'gears',
		title: ES ? 'Acciones de Fila' : 'Row Actions',
		description: ES
			? 'Acciones por informe: Ejecutar (ejecutar ahora), Editar (cambiar configuracion), Clonar (duplicar), Info (detalles e historial) y Eliminar. Ejecutar, Editar, Clonar y Eliminar se ocultan mientras un informe esta en ejecucion (Ejecutar tambien si esta desactivado).'
			: 'Actions per report: Perform (run now), Edit (change config), Clone (duplicate), Info (details & history), and Delete. Perform, Edit, Clone and Delete are hidden while a report is running (Perform also while it is disabled).',
		example: ES
			? 'Ejecutar | Editar | Clonar | Info | Eliminar'
			: 'Perform | Edit | Clone | Info | Delete',
	},
	// ─── 32: Show Report Info button ─────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => (
			document.querySelector('[data-tour-id="tour-show-report-info"]') ||
			document.querySelector('[title="Show Report Info"]')
		),
		// the demo shows the button too; only a real report opens the panel (its steps skip without one)
		skipIf: () => !q('tour-show-report-info'),
		icon: 'circle-info',
		title: ES ? 'Boton Informacion del Informe' : 'Show Report Info Button',
		description: ES
			? 'El boton "i" en la columna Acciones abre el panel de detalles completos del informe. Desde ahi puedes consultar toda la configuracion guardada y el historial de ejecuciones con sus resultados y descargas.'
			: 'The "i" button in the Actions column opens the full report details panel. From there you can view the complete saved configuration and the execution history with its results and downloads.',
		action: () => {
			const firstReport = reportListData.value[0]
			if (firstReport) {
				selectedInfoModalTab.value = INFO_MODAL_TABS.DETAILS
				isOpenInfoModal.value = firstReport.idReport
			}
		},
		waitFor: () => !reportListData.value.length || (!!isOpenInfoModal.value && !!document.querySelector('.MuiDialog-root')),
	},
	// ─── 33: Info Modal — overview ───────────────────────────────────────────────
	{
		id: 'info-start',
		type: 'field',
		// the dialog itself, not its full-screen backdrop
		findTarget: () => document.querySelector('.MuiDialog-root .MuiDialog-paper') || document.querySelector('.MuiDialog-root'),
		skipIf: () => !document.querySelector('.MuiDialog-root'),
		icon: 'rectangle-list',
		title: ES ? 'Panel de Informacion del Informe' : 'Report Info Panel',
		description: ES
			? 'Este modal muestra toda la informacion del informe en dos pestañas: «Details» con la configuracion completa guardada, y «History» con el historial de ejecuciones, sus estados y acciones de descarga. Util para auditar cambios o recuperar resultados anteriores.'
			: 'This modal shows all report information in two tabs: "Details" with the complete saved configuration, and "History" with the execution history, their statuses, and download actions. Useful for auditing changes or retrieving previous results.',
		cleanup: () => { isOpenInfoModal.value = null },
	},
	// ─── 34: Info Modal — Tab Report Details ─────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const dialog = document.querySelector('.MuiDialog-root')
			if (!dialog) return null
			const tabs = dialog.querySelectorAll('[role="tab"]')
			for (const tab of tabs) {
				if (tab.textContent.toLowerCase().includes('details')) return tab
			}
			return null
		},
		skipIf: () => !document.querySelector('.MuiDialog-root'),
		icon: 'memo-circle-info',
		title: ES ? 'Pestaña Details' : 'Details Tab',
		description: ES
			? 'La pestaña «Details» muestra la configuracion completa tal como fue guardada: nombre, sitios, oficinas, moneda, rutas de solo ida, fechas de busqueda, duraciones, horas y dias de ejecucion, emails de notificacion y filtros de exportacion.'
			: 'The "Details" tab shows the complete configuration as saved: name, sites, offices, currency, one-way routes, search dates, durations, execution hours and days, notification emails, and export filters.',
		action: () => { selectedInfoModalTab.value = INFO_MODAL_TABS.DETAILS },
		cleanup: () => { isOpenInfoModal.value = null },
	},
	// ─── 35: Info Modal — Tab Report History ─────────────────────────────────────
	{
		id: 'info-end',
		type: 'field',
		findTarget: () => {
			const dialog = document.querySelector('.MuiDialog-root')
			if (!dialog) return null
			const tabs = dialog.querySelectorAll('[role="tab"]')
			for (const tab of tabs) {
				if (tab.textContent.toLowerCase().includes('history')) return tab
			}
			return null
		},
		skipIf: () => !document.querySelector('.MuiDialog-root'),
		icon: 'rectangle-history',
		title: ES ? 'Pestaña History' : 'History Tab',
		description: ES
			? 'La pestaña «History» muestra todas las ejecuciones anteriores del informe en orden cronologico inverso. Para cada ejecucion ves la fecha, el estado (Listo, Detenido) y los botones de descarga. Perfecto para recuperar resultados de ejecuciones pasadas.'
			: 'The "History" tab shows all previous executions of the report in reverse chronological order. For each execution you see the date, the status (Ready, Stopped), and download buttons. Perfect for retrieving results from past executions.',
		action: () => {
			selectedInfoModalTab.value = INFO_MODAL_TABS.HISTORY
			isOpenInfoModal.value = null
		},
		cleanup: () => { isOpenInfoModal.value = null },
	},
	// ─── 36: Multi-Select ───────────────────────────────────────────────────────
	{
		type: 'field',
		findTarget: () => {
			const checkboxes = document.querySelectorAll('th .MuiCheckbox-root, th input[type="checkbox"]')
			if (checkboxes.length) return checkboxes[0].closest('th')
			return null
		},
		icon: 'list-check',
		title: ES ? 'Seleccion Multiple' : 'Multi-Select',
		description: ES
			? 'Selecciona varios informes de extras y protecciones para aplicar acciones masivas desde una barra flotante en la parte inferior de la pantalla: editar sitios, fechas de recogida, emails, frecuencia o rango de ejecucion, ejecutar, detener, copiar, activar, desactivar o eliminar — todo a la vez.'
			: 'Select multiple extras and protections reports to apply bulk actions from a floating bar at the bottom of the screen: edit sites, pick-up dates, emails, frequency or execution date range, perform, stop, copy, enable, disable, or delete — all at once.',
		example: ES
			? 'Selecciona filas → Barra flotante de acciones'
			: 'Select rows → Floating bar with bulk actions',
	},
	// ─── 37: Outro ──────────────────────────────────────────────────────────────
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo Listo!' : "You're All Set!",
		description: ES
			? 'Crea tu primer informe de extras y protecciones para empezar a monitorizar el precio de los extras y condiciones de cada coche. Usa el boton Info para consultar el historial de ejecucion y descargar informes anteriores.'
			: 'Create your first extras and protections report to start monitoring the price of each car\'s extras and conditions. Use the Info button to check execution history and download past reports.',
	},
]

// ─── Modal ranges ─────────────────────────────────────────────────────────────
// Steps that need the creation drawer or the Info modal open, found by step id so they stay right
// when steps are added. TourOverlay uses them to restore that state when jumping with dots / Back.
const at = id => TOUR_STEPS.findIndex(s => s.id === id)

export const MODAL_RANGES = [
	{
		from: at('drawer-start'), to: at('drawer-end'),
		open: () => reportFormDataInnit(),
		close: () => closeDrawer(),
		isOpen: () => isReportDrawerOpen.value === true,
	},
	{
		from: at('info-start'), to: at('info-end'),
		open: () => {
			const firstReport = reportListData.value[0]
			if (firstReport) {
				selectedInfoModalTab.value = INFO_MODAL_TABS.DETAILS
				isOpenInfoModal.value = firstReport.idReport
			}
		},
		close: () => { isOpenInfoModal.value = null },
		isOpen: () => !!isOpenInfoModal.value && !!document.querySelector('.MuiDialog-root'),
	},
]
